import React, { useContext, useState } from "react";
import moment from "moment";
import classNames from "classnames";
// TODO: This new appointment is calling old jquery code, and we should get rid of it asap
import { CalendarAppointment } from "./CalendarAppointment";
import { CalendarAppointmentModal } from "./CalendarAppointmentModal";
import { AxiosPromise } from "axios";
import { PlanningContext } from "PlanningContext/PlanningContext";

interface DayHeaderProps {
  isFirstDayOfWeek: boolean;
  item: server.dto.AppointmentsDayGroup;
}

const CalendarHeader = (props: DayHeaderProps) => {
  const { isFirstDayOfWeek, item } = props;

  const planningContext = useContext(PlanningContext);
  const date = moment.utc(item.day);

  return <div
    role="button"
    onClick={() => planningContext.open({ date })}
    className={classNames("calendar-template-day-header", {
      today: date.isSame(moment().utc(), "day")
    })}
  >
    <span>{isFirstDayOfWeek && `W${date.week()}`}</span>
    <span style={{ textAlign: "right" }}>{date.format("ddd D MMM")}</span>
  </div>
};

interface CalendarEntryProps {
  entry: server.dto.AppointmentForPlanner;
  isHovered: boolean;
  onHover: (id: number | null) => void;
}

const CalendarEntry = (props: CalendarEntryProps) => {
  const { entry, isHovered, onHover } = props;

  return <div style={{ gridColumn: `span ${entry.durationDays}` }} className="calendar-template-entry">
    <CalendarAppointment appointment={entry} isHovered={isHovered} onHover={onHover} />
  </div>
};

interface WeekProps {
  maxEntriesPerColumn: number;
  minEntriesPerColumn: number;
  items: server.dto.AppointmentsDayGroup[];
  hoveredId: number | null;
  onHover: (id: number | null) => void;
  filterSet: server.dto.FilterSets;
  fetchFunction: (day: moment.Moment) => AxiosPromise<server.dto.ApiResponseWrapperWithRequest<server.dto.AppointmentForPlanner[], any>>;
}

const CalendarWeek = (props: WeekProps) => {
  const { items, maxEntriesPerColumn, minEntriesPerColumn, filterSet, fetchFunction } = props;
  const [openDayModal, setOpenDayModal] = React.useState<server.dto.AppointmentsDayGroup | null>(null);

  const longestDay = Math.max(...items.map(item => item.count));
  // We want to be able to limit the number of entries per column so the height is not to big, and at the same time
  //  We want to leave a minimum number of rows to make the calendar look good
  const rowCount = Math.max(Math.min(longestDay, maxEntriesPerColumn), minEntriesPerColumn);
  const hasMore = longestDay > maxEntriesPerColumn;

  return <div className="calendar-template-week">
    {items.map((item, index) => <CalendarHeader
      isFirstDayOfWeek={index === 0}
      key={item.day as any}
      item={item}
    />)}
    {Array.from({ length: rowCount }).map((_, rowIndex) =>
      items.map((item, index) => {
        if (rowIndex >= item.items.length) {
          return <div key={`calendar-${rowIndex}-${index}`} className="calendar-template-entry" />;
        }

        if (item.items[rowIndex] === null) {
          return <div key={`calendar-${rowIndex}-${index}`} className="calendar-template-entry" />;
        }

        if (!item.items[rowIndex].isStartOfCalendarEntry) {
          return null;
        }

        return <CalendarEntry
          key={`calendar-entry-${item.items[rowIndex].type.value}-${item.items[rowIndex].id}`}
          entry={item.items[rowIndex]}
          isHovered={props.hoveredId === item.items[rowIndex].id}
          onHover={props.onHover}
        />;
      })
    )}
    {hasMore && items.map((item, index) => <div key={`more-${index}`} className="calendar-template-more-entry">
      {item.count > maxEntriesPerColumn && <div className="calendar-template-more" role="button" onClick={() => setOpenDayModal(item)}>
        {`${RESX.Planning.More} +${item.count - maxEntriesPerColumn}`}
      </div>}
    </div>)}
    {openDayModal && <CalendarAppointmentModal
      onClose={() => setOpenDayModal(null)}
      scheduleForDay={openDayModal}
      filterSet={filterSet}
      fetchFunction={fetchFunction}
    />}
  </div>
};

interface CalendarProps {
  items: server.dto.AppointmentsDayGroup[];
  maxEntriesPerColumn: number;
  filterSet: server.dto.FilterSets;
  fetchFunction: (day: moment.Moment) => AxiosPromise<server.dto.ApiResponseWrapperWithRequest<server.dto.AppointmentForPlanner[], any>>;
}

export const CalendarTemplate = (props: CalendarProps) => {
  const { items, maxEntriesPerColumn, filterSet, fetchFunction } = props;
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  const onHover = (id: number | null) => {
    setHoveredId(id);
  }

  const weeks = items.reduce((acc, _, index) => {
    if (index % 7 === 0) {
      acc.push(items.slice(index, index + 7));
    }
    return acc;
  }, [] as server.dto.AppointmentsDayGroup[][]);

  return <div className="calendar-template-wrapper">
    {weeks.map((week, index) => <CalendarWeek
      key={index}
      items={week}
      hoveredId={hoveredId}
      onHover={onHover}
      maxEntriesPerColumn={maxEntriesPerColumn}
      minEntriesPerColumn={3}
      filterSet={filterSet}
      fetchFunction={fetchFunction}
    />)}
  </div>
};